import { useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/Button";
import { Field, Input } from "@/components/Input";
import { useAuth } from "./useAuth";

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Connexion impossible. Vérifiez le réseau et réessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <VaultMark />
          <div>
            <p className="text-xl font-bold tracking-tight text-ink">HD-VAULT</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
              Inventaire · Outils · Projets
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5"
        >
          <div>
            <h1 className="text-[15px] font-semibold text-ink">Connexion</h1>
            <p className="mt-1 text-[13px] text-ink-subtle">
              Accédez à votre inventaire.
            </p>
          </div>

          <Field label="Courriel" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="prenom@exemple.ca"
            />
          </Field>

          <Field label="Mot de passe" htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          {error && (
            <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-[13px] text-danger">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
            Se connecter
          </Button>
        </form>
      </div>
    </div>
  );
}

export function VaultMark({ className = "size-10" }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <rect width="32" height="32" rx="7" className="fill-surface-raised" />
      <path
        d="M16 5 26 10.5v11L16 27 6 21.5v-11L16 5z"
        fill="none"
        className="stroke-brand"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M16 5v22M6 10.5l10 5.5 10-5.5"
        fill="none"
        className="stroke-brand"
        strokeWidth="1.6"
        strokeLinejoin="round"
        opacity=".55"
      />
    </svg>
  );
}
